import React from 'react';
import { Card, CardContent, Box, Typography, Button } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import backLogo from '../../asset/images/iStock-1044717842.jpg';
import { AgentMarketPlaceItemWithFilter } from '../../pages/userAgent/agentMarketPlace/AgentMarketPlace';
import { useGetGoldCertificateListQuery, useSetGoldCertificateMutation } from '../../data/api/endPointsSlice';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';

interface AgentCardProps {
  agent: AgentMarketPlaceItemWithFilter;
  onEdit: (agent: AgentMarketPlaceItemWithFilter) => void;
  onDelete: (agentId: string, agentName?: string) => Promise<void>;
}

const AgentCard: React.FC<AgentCardProps> = ({ agent, onEdit, onDelete }) => {
  // Fetch gold certificate list from API
  const { data: goldCertResponse, refetch } = useGetGoldCertificateListQuery();
  let goldList: any[] = [];
  if (Array.isArray(goldCertResponse)) {
    goldList = goldCertResponse;
  } else if (
    goldCertResponse &&
    typeof goldCertResponse === 'object' &&
    'certificates' in goldCertResponse &&
    Array.isArray((goldCertResponse as any).certificates)
  ) {
    goldList = (goldCertResponse as any).certificates;
  }
  const [setGoldCertificate, { isLoading: isSettingGold }] = useSetGoldCertificateMutation();
  // goldList is expected to be an array of agent IDs or objects with _id
  const [optimisticGold, setOptimisticGold] = React.useState<null | boolean>(null);
  React.useEffect(() => {
    if (goldList && Array.isArray(goldList)) {
      // goldList is now an array of { agent_id, gold_rating }
      const goldObj = goldList.find((item: any) => item.agent_id === agent._id);
      setOptimisticGold(goldObj ? !!goldObj.gold_rating : false);
    } else {
      setOptimisticGold(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(goldList), agent._id]);

  // Handler for toggle (optimistic UI)
  const handleGoldToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setOptimisticGold(newValue);
    try {
      await setGoldCertificate({ agentId: agent._id, gold_rating: newValue }).unwrap();
      refetch(); // Refresh gold list after change
    } catch (e) {
      // Revert if error
      setOptimisticGold(prev => !prev);
    }
  };

  return (
    <Box
      sx={{
        flex: {
          xs: '1 1 100%',
          sm: '1 1 48%',
          md: '1 1 32%',
          lg: '1 1 19%',
        },
        maxWidth: { xs: '100%', sm: '48%', md: '32%', lg: '19%' },
        minWidth: 260,
        mb: 2,
      }}
    >
      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          boxShadow: 3,
          border: '1px solid #ddd',
          textAlign: 'left',
          p: 0,
          bgcolor: '#fff',
          overflow: 'hidden',
          minHeight: 400,
          maxHeight: 420,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Banner Image */}
        <Box
          sx={{
            width: '100%',
            height: 100,
            background: 'linear-gradient(90deg, #0d47a1 0%, #1976d2 100%)',
            backgroundImage: `url(${backLogo})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            position: 'relative',
          }}
        />
        <CardContent sx={{ pt: 5, position: 'relative' }}>
          {/* Category chip */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, mb: 2 }}>
            <Box sx={{ bgcolor: '#f2f2f2', px: 2, py: 0.5, borderRadius: 2, fontSize: 14, fontWeight: 500 }}>{agent.category}</Box>
          </Box>
          <Typography
            variant="caption"
            color="text.primary"
            fontWeight="bold"
            sx={{
              fontSize: 14,
              mb: 1,
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 180,
              display: 'block',
              pr: optimisticGold ? 4 : 0 // Add padding if gold is present
            }}
          >
            {agent.name}
          </Typography>
          {/* Only show these details below */}
          <Typography color="text.secondary" sx={{ fontSize: 14, mb: 1, textAlign: 'left' }}>
            <strong>ID:</strong> {agent._id}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 14, mb: 1, textAlign: 'left' }}>
            <strong>Owner:</strong> {agent.owners && agent.owners.length > 0 ? agent.owners[0].name : 'N/A'}
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-start' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                sx={{ minWidth: 0, p: 1, borderRadius: '50%' }}
                onClick={() => onEdit(agent)}
              >
                <EditOutlinedIcon fontSize="small" />
              </Button>
              <Button
                color="error"
                size="small"
                sx={{ minWidth: 0, p: 1, borderRadius: '50%' }}
                onClick={() => onDelete(agent._id, agent.name)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </Button>
              {/* Gold Rating Toggle - visible below banner, above category */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, justifyContent: 'flex-end', minHeight: 40 }}>
                <Tooltip title={optimisticGold ? 'Gold Rating Enabled' : 'Enable Gold Rating'}>
                  <Switch
                    checked={!!optimisticGold}
                    onChange={handleGoldToggle}
                    color="warning"
                    disabled={isSettingGold}
                    inputProps={{ 'aria-label': 'Gold rating toggle' }}
                  />
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
export default AgentCard;
